import { useEffect, useState } from 'react';
import type { Lot } from '../data/mockLots';
import { useCountdown, formatStartsIn } from '../hooks/useCountdown';
import type { LotState } from '../hooks/useLotState';
import { RingTimer } from './RingTimer';
import { SkinFrame } from './SkinFrame';

const RARITY_LABEL: Record<string, { label: string; color: string }> = {
  consumer: { label: 'CONSUMER', color: '#b0c3d9' },
  industrial: { label: 'INDUSTRIAL', color: '#5e98d9' },
  milspec: { label: 'MIL-SPEC', color: '#4b69ff' },
  restricted: { label: 'RESTRICTED', color: '#8847ff' },
  classified: { label: 'CLASSIFIED', color: '#d32ce6' },
  covert: { label: 'COVERT', color: '#eb4b4b' },
  contraband: { label: 'CONTRABAND', color: '#e4ae39' },
};

const WEAR_LABEL: Record<string, string> = {
  FN: 'Factory New',
  MW: 'Minimal Wear',
  FT: 'Field-Tested',
  WW: 'Well-Worn',
  BS: 'Battle-Scarred',
};

const STATE_BADGE: Record<LotState, { label: string; color: string; dot: string }> = {
  live: { label: 'LIVE', color: '#ff4d4d', dot: 'bg-danger' },
  scheduled: { label: 'SCHEDULED', color: '#ffd93d', dot: 'bg-warning' },
  closed: { label: 'CLOSED', color: '#7a8595', dot: 'bg-fg-muted' },
};

type Props = {
  lot: Lot;
  state: LotState;
  livePrice?: number;
  liveBids?: number;
  flashTrigger?: number;
  timerEndsAt?: number;
};

export function LotHero({ lot, state, livePrice, liveBids, flashTrigger = 0, timerEndsAt }: Props) {
  const liveSeconds = useCountdown(timerEndsAt ?? lot.timerEndsAt);
  const startsIn = useCountdown(lot.startsAt ?? lot.timerEndsAt);

  const rarity = RARITY_LABEL[lot.rarity] ?? { label: lot.rarity.toUpperCase(), color: '#7a8595' };
  const wearLabel = WEAR_LABEL[lot.wear] ?? lot.wear;
  const badge = STATE_BADGE[state];

  const shownPrice = state === 'closed' ? (lot.finalPrice ?? lot.currentPrice) : (livePrice ?? lot.currentPrice);
  const shownBids = liveBids ?? lot.bidsCount;
  const discountPct = Math.round((1 - shownPrice / lot.marketPrice) * 100);

  return (
    <section className="relative hud-corners overflow-hidden">
      <div className="relative" style={{ aspectRatio: '4 / 3' }}>
        <div className="absolute inset-0">
          <SkinFrame weapon={lot.weapon} palette={lot.palette} size="lg" />
        </div>

        <div
          className="absolute inset-0 pointer-events-none scanlines mix-blend-overlay"
          style={{ opacity: state === 'closed' ? 0.25 : 0.55 }}
        />

        {state === 'live' && (
          <div className="absolute inset-x-0 top-1/3 h-12 overflow-hidden pointer-events-none">
            <div
              className="absolute inset-y-0 anim-sweep"
              style={{
                width: '38%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
              }}
            />
          </div>
        )}

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,14,20,0.55) 0%, rgba(10,14,20,0.05) 38%, rgba(10,14,20,0.92) 100%)',
          }}
        />

        <div className="relative z-10 h-full flex flex-col">
          <div className="flex items-start justify-between p-4 pb-2 gap-3">
            <div className="flex flex-col gap-1.5 min-w-0">
              <span
                className={`font-mono text-[10px] tracking-[0.3em] flex items-center gap-1.5 ${state === 'live' ? 'anim-blink' : ''}`}
                style={{ color: badge.color }}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                {badge.label}
              </span>
              <span
                className="font-mono text-[10px] tracking-[0.2em]"
                style={{ color: rarity.color, opacity: 0.85 }}
              >
                {rarity.label} · {lot.wear} · float {lot.float.toFixed(2)}
              </span>
              <span className="font-mono text-[9px] tracking-[0.18em] text-fg-dim">
                {wearLabel}
              </span>
            </div>

            <div className="flex flex-col items-end gap-1 shrink-0">
              {state === 'live' && (
                <RingTimer
                  secondsLeft={liveSeconds}
                  size={96}
                  stroke={5}
                  scaleSec={60}
                  flashTrigger={flashTrigger}
                  showLabel
                  label="TIMER"
                />
              )}
              {state === 'scheduled' && (
                <div className="flex flex-col items-end">
                  <span className="font-mono text-[9px] tracking-[0.25em] text-fg-muted">OPENS IN</span>
                  <span
                    className={`font-mono font-bold tabular-nums leading-none mt-1 ${startsIn <= 30 ? 'text-warning' : 'text-primary'}`}
                    style={{ fontSize: 30, textShadow: '0 0 14px rgba(0,229,255,0.4)' }}
                  >
                    {formatStartsIn(startsIn)}
                  </span>
                </div>
              )}
              {state === 'closed' && (
                <div className="flex flex-col items-end">
                  <span className="font-mono text-[9px] tracking-[0.25em] text-fg-dim">TIMER</span>
                  <span
                    className="font-mono font-bold leading-none mt-1 text-fg-muted"
                    style={{ fontSize: 26 }}
                  >
                    CLOSED
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-auto px-4 pb-4 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h2
                className="font-display font-bold leading-[0.92] tracking-tight text-fg truncate"
                style={{ fontSize: 38 }}
              >
                {lot.weaponLabel}
              </h2>
              <h3
                className="font-display font-medium leading-tight mt-1 truncate"
                style={{ fontSize: 18, color: lot.palette.accent ?? lot.palette.to }}
              >
                | {lot.skinName}
              </h3>
            </div>

            <div className="flex flex-col items-end shrink-0">
              <span className="font-mono text-[9px] tracking-[0.25em] text-fg-muted">
                {state === 'closed' ? 'FINAL' : 'CURRENT'}
              </span>
              <span
                className="font-display font-bold tabular-nums leading-none mt-1"
                style={{
                  fontSize: 30,
                  color: state === 'closed' ? '#e6edf3' : '#00ff94',
                  textShadow: state === 'closed' ? 'none' : '0 0 14px rgba(0,255,148,0.35)',
                }}
              >
                ${shownPrice.toFixed(2)}
              </span>
              <span className="font-mono text-[10px] tracking-wider text-fg-muted mt-1">
                <span className="line-through opacity-70">${lot.marketPrice.toFixed(2)}</span>
                <span className="text-success ml-1.5">−{discountPct}%</span>
              </span>
              {state === 'live' && (
                <span className="font-mono text-[10px] tracking-wider text-fg-muted mt-0.5">
                  {shownBids} bids
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
